import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Slider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Visibility,
  Edit,
  Person,
  Assignment,
  Star,
  AccountBalance,
  Save,
  Cancel,
  ExpandMore,
  FilterList,
  Sort
} from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import VerificationReviewDialog from '../../components/admin/VerificationReviewDialog';


export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [selectedVerificationUser, setSelectedVerificationUser] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [userDetails, setUserDetails] = useState({
    id: '',
    name: '',
    email: '',
    tier: 'Basic',
    balance: 0,
    tasks_completed: 0,
    rating: 0,
    email_verified: false,
    admin_notes: '',
    verification_status: 'not_started',
    verification_level: 'basic',
    last_verification_attempt: null
  });
  const [editFormData, setEditFormData] = useState({
    tier: '',
    balance_adjustment: '',
    admin_notes: '',
    email_verified: false,
    reason: ''
  });
  const [saveLoading, setSaveLoading] = useState(false);
    const [filters, setFilters] = useState({
    tier: 'all',
    verified: 'all',
    minRating: '',
    minTasks: ''
  });
  const [sorting, setSorting] = useState({
    sortBy: 'created_at',
    sortOrder: 'desc'
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Fetch users data
const fetchUsers = async (page = 0, search = '', filters = {}, sorting = {}) => {
  try {
    setLoading(true);
    const token = localStorage.getItem('adminToken');
    
    // Build query parameters
    const params = new URLSearchParams({
      page: (page + 1).toString(),
      limit: pagination.limit.toString(),
      search: search,
      sortBy: sorting.sortBy || 'created_at',
      sortOrder: sorting.sortOrder || 'desc',
      tier: filters.tier || 'all',
      verified: filters.verified || 'all',
      ...(filters.minRating && { min_rating: filters.minRating }),
      ...(filters.minTasks && { min_tasks: filters.minTasks })
    });

    const response = await fetch(
      `https://propertyearn.net/api/api/admin/users?${params}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }

    const data = await response.json();
    setUsers(data.users);
    setPagination(data.pagination);
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

  // Fetch user details
const fetchUserDetails = async (userId) => {
  if (!userId) {
    setError('User ID is required');
    return;
  }

  try {
    setActionLoading(true);
    const token = localStorage.getItem('adminToken');
    
    if (!token) {
      setError('Admin token not found');
      setActionLoading(false);
      return;
    }

    const response = await fetch(
      `https://propertyearn.net/api/api/admin/users/${userId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch user details');
    }

    const data = await response.json();
    
    // Ensure userDetails has proper structure with id
    setUserDetails({
      id: data.id || userId, // Use the userId from parameter as fallback
      name: data.name || '',
      email: data.email || '',
      tier: data.tier || 'Basic',
      balance: data.balance || 0,
      tasks_completed: data.tasks_completed || 0,
      rating: data.rating || 0,
      email_verified: data.email_verified || false,
      admin_notes: data.admin_notes || '',
      verification_status: data.verification_status || 'not_started',
      verification_level: data.verification_level || 'basic',
      last_verification_attempt: data.last_verification_attempt || null,
      ...data // Spread the rest of the data
    });
    
    setDetailsDialogOpen(true);
  } catch (err) {
    setError(err.message);
    // Set a minimal userDetails object with at least an id
    setUserDetails({
      id: userId,
      name: 'Error loading user',
      email: '',
      verification_status: 'error'
    });
  } finally {
    setActionLoading(false);
  }
};


// Add this function to your component
const handleReviewVerification = (userId) => {
  if (!userId) {
    setError('User ID is required for verification review');
    return;
  }
    const userToVerify = users.find(user => user.id === userId) || userDetails;

  console.log('Review verification for user:', userId);
  // This will open your verification review dialog
  // You'll implement this later

   if (userToVerify) {
    setSelectedVerificationUser(userToVerify);
    setVerificationDialogOpen(true);
  } else {
    setError('User not found for verification review');
  }
};

  useEffect(() => {
    fetchUsers(0, searchTerm, filters, sorting);
  }, []);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    fetchUsers(0, event.target.value);
  };

  const handlePageChange = (event, newPage) => {
    fetchUsers(newPage, searchTerm);
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleViewDetails = () => {
    if (selectedUser) {
      fetchUserDetails(selectedUser.id);
    }
    handleMenuClose();
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'Pro': return 'success';
      case 'Verified': return 'primary';
      default: return 'default';
    }
  };


  // Handle opening edit dialog
const handleEditUser = () => {
  if (selectedUser) {
    setEditingUser(selectedUser);
    setEditFormData({
      tier: selectedUser.tier,
      balance_adjustment: '',
      admin_notes: userDetails?.admin_notes || '',
      email_verified: userDetails?.email_verified || false,
      reason: ''
    });
    setEditDialogOpen(true);
  }
  handleMenuClose();
};

// Handle saving user changes
const handleSaveUser = () => {
    const needsConfirmation = editFormData.balance_adjustment || 
                            editFormData.tier !== editingUser.tier;
    
    if (needsConfirmation) {
        setPendingAction({ type: 'update user', callback: executeSaveUser });
        setConfirmDialogOpen(true);
    } else {
        executeSaveUser();
    }
    };

    const executeSaveUser = async () => {

  try {
    setSaveLoading(true);
    const token = localStorage.getItem('adminToken');
    
    const response = await fetch(
      `https://propertyearn.net/api/api/admin/users/${editingUser.id}`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editFormData)
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update user');
    }

    const result = await response.json();
    
    // Show success message
    setError('');
    alert(`User updated successfully! Changes: ${result.changes.join(', ')}`);
    
    // Refresh user data
    fetchUsers(pagination.page, searchTerm);
    setEditDialogOpen(false);
    
  } catch (err) {
    setError(err.message);
  } finally {
    setSaveLoading(false);
  }
};

    // Handle confirmation
    const handleConfirmAction = () => {
    setConfirmDialogOpen(false);
    if (pendingAction?.callback) {
        pendingAction.callback();
        }
    };

  if (loading) {

    return (
      <AdminLayout pageTitle="User Management">
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

        return (
        <AdminLayout pageTitle="User Management">
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Search Bar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

          {/* Filters and Sorting Bar */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Accordion expanded={filtersExpanded} onChange={() => setFiltersExpanded(!filtersExpanded)}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FilterList />
                  <Typography>Filters & Sorting</Typography>
                  {/* Show active filter count */}
                  {(filters.tier !== 'all' || filters.verified !== 'all' || filters.minRating || filters.minTasks) && (
                    <Chip label="Active" color="primary" size="small" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  {/* Tier Filter */}
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Tier</InputLabel>
                      <Select
                        value={filters.tier}
                        label="Tier"
                        onChange={(e) => setFilters({...filters, tier: e.target.value})}
                      >
                        <MenuItem value="all">All Tiers</MenuItem>
                        <MenuItem value="Basic">Basic</MenuItem>
                        <MenuItem value="Verified">Verified</MenuItem>
                        <MenuItem value="Pro">Pro</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Verification Filter */}
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Email Verified</InputLabel>
                      <Select
                        value={filters.verified}
                        label="Email Verified"
                        onChange={(e) => setFilters({...filters, verified: e.target.value})}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="verified">Verified Only</MenuItem>
                        <MenuItem value="unverified">Unverified Only</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Rating Filter */}
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Min Rating</InputLabel>
                      <Select
                        value={filters.minRating}
                        label="Min Rating"
                        onChange={(e) => setFilters({...filters, minRating: e.target.value})}
                      >
                        <MenuItem value="">Any Rating</MenuItem>
                        <MenuItem value="3">3+ Stars</MenuItem>
                        <MenuItem value="4">4+ Stars</MenuItem>
                        <MenuItem value="4.5">4.5+ Stars</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Tasks Filter */}
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Min Tasks</InputLabel>
                      <Select
                        value={filters.minTasks}
                        label="Min Tasks"
                        onChange={(e) => setFilters({...filters, minTasks: e.target.value})}
                      >
                        <MenuItem value="">Any</MenuItem>
                        <MenuItem value="5">5+ Tasks</MenuItem>
                        <MenuItem value="10">10+ Tasks</MenuItem>
                        <MenuItem value="20">20+ Tasks</MenuItem>
                        <MenuItem value="50">50+ Tasks</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Sorting */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Sort />
                      <FormControl sx={{ minWidth: 120 }}>
                        <InputLabel>Sort By</InputLabel>
                        <Select
                          value={sorting.sortBy}
                          label="Sort By"
                          onChange={(e) => setSorting({...sorting, sortBy: e.target.value})}
                        >
                          <MenuItem value="created_at">Join Date</MenuItem>
                          <MenuItem value="name">Name</MenuItem>
                          <MenuItem value="rating">Rating</MenuItem>
                          <MenuItem value="tasks_completed">Tasks Completed</MenuItem>
                          <MenuItem value="balance">Balance</MenuItem>
                          <MenuItem value="last_login">Last Login</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl sx={{ minWidth: 100 }}>
                        <InputLabel>Order</InputLabel>
                        <Select
                          value={sorting.sortOrder}
                          label="Order"
                          onChange={(e) => setSorting({...sorting, sortOrder: e.target.value})}
                        >
                          <MenuItem value="asc">Ascending</MenuItem>
                          <MenuItem value="desc">Descending</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>

                  {/* Action Buttons */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setFilters({ tier: 'all', verified: 'all', minRating: '', minTasks: '' });
                          setSorting({ sortBy: 'created_at', sortOrder: 'desc' });
                        }}
                      >
                        Reset Filters
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => fetchUsers(0, searchTerm, filters, sorting)}
                      >
                        Apply Filters
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* component to show active filters */}
            {(filters.tier !== 'all' || filters.verified !== 'all' || filters.minRating || filters.minTasks) && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>Active Filters:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {filters.tier !== 'all' && (
                    <Chip 
                      label={`Tier: ${filters.tier}`} 
                      onDelete={() => setFilters({...filters, tier: 'all'})}
                      size="small"
                    />
                  )}
                  {filters.verified !== 'all' && (
                    <Chip 
                      label={`Verified: ${filters.verified}`} 
                      onDelete={() => setFilters({...filters, verified: 'all'})}
                      size="small"
                    />
                  )}
                  {filters.minRating && (
                    <Chip 
                      label={`Min Rating: ${filters.minRating}+`} 
                      onDelete={() => setFilters({...filters, minRating: ''})}
                      size="small"
                    />
                  )}
                  {filters.minTasks && (
                    <Chip 
                      label={`Min Tasks: ${filters.minTasks}+`} 
                      onDelete={() => setFilters({...filters, minTasks: ''})}
                      size="small"
                    />
                  )}
                  <Chip 
                    label={`Sorted by: ${sorting.sortBy} (${sorting.sortOrder})`} 
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Paper>
            )}
          </Paper>

        {/* Users Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        onClick={() => setSorting({
                          sortBy: 'name',
                          sortOrder: sorting.sortBy === 'name' && sorting.sortOrder === 'asc' ? 'desc' : 'asc'
                        })}>
                      User
                      {sorting.sortBy === 'name' && (
                        <Sort fontSize="small" 
                              sx={{ 
                                transform: sorting.sortOrder === 'desc' ? 'rotate(180deg)' : 'none',
                                ml: 0.5 
                              }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        onClick={() => setSorting({
                          sortBy: 'tier',
                          sortOrder: sorting.sortBy === 'tier' && sorting.sortOrder === 'asc' ? 'desc' : 'asc'
                        })}>
                      Tier
                      {sorting.sortBy === 'tier' && (
                        <Sort fontSize="small" 
                              sx={{ 
                                transform: sorting.sortOrder === 'desc' ? 'rotate(180deg)' : 'none',
                                ml: 0.5 
                              }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        onClick={() => setSorting({
                          sortBy: 'tasks_completed',
                          sortOrder: sorting.sortBy === 'tasks_completed' && sorting.sortOrder === 'asc' ? 'desc' : 'asc'
                        })}>
                      Tasks
                      {sorting.sortBy === 'tasks_completed' && (
                        <Sort fontSize="small" 
                              sx={{ 
                                transform: sorting.sortOrder === 'desc' ? 'rotate(180deg)' : 'none',
                                ml: 0.5 
                              }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        onClick={() => setSorting({
                          sortBy: 'rating',
                          sortOrder: sorting.sortBy === 'rating' && sorting.sortOrder === 'asc' ? 'desc' : 'asc'
                        })}>
                      Rating
                      {sorting.sortBy === 'rating' && (
                        <Sort fontSize="small" 
                              sx={{ 
                                transform: sorting.sortOrder === 'desc' ? 'rotate(180deg)' : 'none',
                                ml: 0.5 
                              }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        onClick={() => setSorting({
                          sortBy: 'balance',
                          sortOrder: sorting.sortBy === 'balance' && sorting.sortOrder === 'asc' ? 'desc' : 'asc'
                        })}>
                      Balance
                      {sorting.sortBy === 'balance' && (
                        <Sort fontSize="small" 
                              sx={{ 
                                transform: sorting.sortOrder === 'desc' ? 'rotate(180deg)' : 'none',
                                ml: 0.5 
                              }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Box>
                        <Typography fontWeight="bold">{user.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={user.tier} color={getTierColor(user.tier)} size="small" />
                    </TableCell>
                    <TableCell>{user.tasks_completed}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Star fontSize="small" color="warning" />
                        <Typography sx={{ ml: 0.5 }}>{user.rating}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>£{user.balance.toFixed(2)}</TableCell>
                    <TableCell>
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, user)}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={pagination.total}
            rowsPerPage={pagination.limit}
            page={pagination.page - 1}
            onPageChange={handlePageChange}
            onRowsPerPageChange={(e) => {
              setPagination({ ...pagination, limit: parseInt(e.target.value) });
              fetchUsers(0, searchTerm);
            }}
          />
        </Paper>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleViewDetails}>
            <Visibility sx={{ mr: 1 }} /> View Details
          </MenuItem>
          <MenuItem onClick={handleEditUser}>
            <Edit sx={{ mr: 1 }} /> Edit User
          </MenuItem>
        </Menu>

              {/* User Details Dialog */}
              <Dialog 
                open={detailsDialogOpen} 
                onClose={() => setDetailsDialogOpen(false)}
                maxWidth="md"
                fullWidth
              >
                <DialogTitle>User Details</DialogTitle>
                <DialogContent>
                  {userDetails ? (
                    <Grid container spacing={3}>
                      {/* Basic Information */}
                      <Grid item xs={12} md={6}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>Basic Information</Typography>
                            <Typography><strong>Name:</strong> {userDetails.name || 'N/A'}</Typography>
                            <Typography><strong>Email:</strong> {userDetails.email || 'N/A'}</Typography>
                            <Typography><strong>Tier:</strong> {userDetails.tier || 'N/A'}</Typography>
                            <Typography><strong>Rating:</strong> {userDetails.rating || 'N/A'}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Statistics */}
                      <Grid item xs={12} md={6}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>Statistics</Typography>
                            <Typography><strong>Tasks Completed:</strong> {userDetails.tasks_completed || 0}</Typography>
                            <Typography><strong>Balance:</strong> £{(userDetails.balance || 0).toFixed(2)}</Typography>
                            <Typography><strong>Last Login:</strong> {userDetails.last_login ? new Date(userDetails.last_login).toLocaleString() : 'Never'}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Verification Status */}
                      <Grid item xs={12}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>Verification Status</Typography>
                            <Typography>
                              <strong>Status:</strong> {userDetails.verification_status || 'Not Started'}
                            </Typography>
                            <Typography>
                              <strong>Level:</strong> {userDetails.verification_level || 'Basic'}
                            </Typography>
                            <Typography>
                              <strong>Last Attempt:</strong> {userDetails.last_verification_attempt ? 
                                new Date(userDetails.last_verification_attempt).toLocaleString() : 'Never'}
                            </Typography>
                            
                            {/* FIXED: Use userDetails.id instead of selectedUser.id */}
                            {userDetails && userDetails.verification_status === 'pending' && (
                          <Button 
                            variant="contained" 
                            color="primary" 
                            sx={{ mt: 2 }}
                            onClick={() => handleReviewVerification(userDetails.id)}
                            disabled={!userDetails.id}
                          >
                            Review Verification
                          </Button>
                        )}
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Admin Information */}
                      <Grid item xs={12}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>Admin Information</Typography>
                            <Typography>
                              <strong>Admin Notes:</strong> {userDetails.admin_notes || 'No notes'}
                            </Typography>
                            <Typography>
                              <strong>Email Verified:</strong> {userDetails.email_verified ? 'Yes' : 'No'}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                      <CircularProgress />
                    </Box>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
                </DialogActions>
              </Dialog>


        {/* Edit User Dialog */}
            <Dialog 
            open={editDialogOpen} 
            onClose={() => setEditDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            >
            <DialogTitle>
                Edit User: {editingUser?.name}
            </DialogTitle>
            <DialogContent>
                {editingUser && (
                <Box component="form" sx={{ mt: 2 }}>
        {/* Tier Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>User Tier</InputLabel>
          <Select
            value={editFormData.tier}
            label="User Tier"
            onChange={(e) => setEditFormData({...editFormData, tier: e.target.value})}
          >
            <MenuItem value="Basic">Basic</MenuItem>
            <MenuItem value="Verified">Verified</MenuItem>
            <MenuItem value="Pro">Pro</MenuItem>
          </Select>
        </FormControl>

        {/* Balance Adjustment */}
        <TextField
          fullWidth
          label="Balance Adjustment (£)"
          type="number"
          value={editFormData.balance_adjustment}
          onChange={(e) => setEditFormData({...editFormData, balance_adjustment: e.target.value})}
          sx={{ mb: 2 }}
          helperText="Positive to add funds, negative to deduct"
        />

        {/* Email Verification Status */}
        <FormControlLabel
          control={
            <Switch
              checked={editFormData.email_verified}
              onChange={(e) => setEditFormData({...editFormData, email_verified: e.target.checked})}
            />
          }
          label="Email Verified"
          sx={{ mb: 2 }}
        />

        {/* Admin Notes */}
        <TextField
          fullWidth
          label="Admin Notes"
          multiline
          rows={3}
          value={editFormData.admin_notes}
          onChange={(e) => setEditFormData({...editFormData, admin_notes: e.target.value})}
          sx={{ mb: 2 }}
          helperText="Internal notes about this user"
        />

        {/* Reason for Changes */}
        <TextField
          fullWidth
          label="Reason for Changes"
          multiline
          rows={2}
          value={editFormData.reason}
          onChange={(e) => setEditFormData({...editFormData, reason: e.target.value})}
          helperText="Required for audit purposes"
        />
      </Box>
        )}
        </DialogContent>
        <DialogActions>
            <Button 
            onClick={() => setEditDialogOpen(false)}
            startIcon={<Cancel />}
            disabled={saveLoading}
            >
            Cancel
            </Button>
            <Button 
            onClick={handleSaveUser}
            variant="contained"
            startIcon={saveLoading ? <CircularProgress size={16} /> : <Save />}
            disabled={saveLoading || !editFormData.reason.trim()}
            >
            {saveLoading ? 'Saving...' : 'Save Changes'}
            </Button>
        </DialogActions>
        </Dialog>

    <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Action</DialogTitle>
            <DialogContent>
            <Typography>
            Are you sure you want to {pendingAction?.type}? This action will be logged for audit purposes.
            </Typography>
            {Math.abs(parseFloat(editFormData.balance_adjustment)) > 50 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
                Large balance adjustment detected. Please ensure this is correct.
            </Alert>
            )}
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
            <Button 
            onClick={handleConfirmAction}
            variant="contained" 
            color="primary"
            >
            Confirm
            </Button>
        </DialogActions>
        </Dialog>

              <VerificationReviewDialog
                open={verificationDialogOpen}
                onClose={() => setVerificationDialogOpen(false)}
                user={selectedVerificationUser}
                onVerificationUpdate={() => {
                  // Refresh user data after verification action
                  fetchUsers(pagination.page, searchTerm, filters, sorting);
                  if (userDetails) {
                    fetchUserDetails(userDetails.id); // Refresh details if open
                  }
                }}
              />
      </Box>
    </AdminLayout>
  );
}